import { FlowTypes } from '@interfaces/models/flow-types';
import { IndividualDesignationListedTypes } from '@interfaces/models/individual-designation-types';
import { ViadocExpectableLabel, ViadocUploadStep } from '@interfaces/models/viadoc-types';
import { createSelector } from '@ngrx/store';
import {
  ExpectedLabelInputs,
  resolveExpectedLabels,
} from '@src/app/secure/document-upload/helpers/resolve-expected-labels.helper';

import { IFileMetaData } from '../../reducers/document-upload/document-upload.reducer';
import {
  selectDecedentInheritanceWaiverState,
  selectIsDeathVerified,
} from '../decedent-info/decedent-info.selectors';
import {
  selectDocumentUploadAccountTypes,
  selectFilesMetadata,
} from '../document-upload/document-upload.selectors';
import {
  selectCwpInheritanceSaveStateToggle,
  selectMultiInheritanceEstateToggle,
  selectViadocShadowModeToggle,
} from '../feature/feature.selectors';
import { selectLetterFilesMetadata } from '../letter-upload/letter-upload.selectors';
import { TAX_DOCUMENTATION_REQUIRED_STATES } from '../tax-document-upload/tax-document-upload.helpers';
import { selectTaxFilesMetadata } from '../tax-document-upload/tax-document-upload.selectors';
import {
  flowTypeSelection,
  selectIndividualDesignation,
  selectIsDivorceFlow,
  selectIsIRASingleExecutor,
  selectIsMultiInheritanceFlow,
  selectIsTrustee,
  selectIsTrustToTrustFlow,
  selectTypeOfTrust,
} from '../triage/triage.selectors';

export interface ViadocScanSlice {
  uploadStep: ViadocUploadStep;
  expectedTypes: ViadocExpectableLabel[];
  files: IFileMetaData[];
}

export interface ViadocScanPlan {
  toggleOn: boolean;
  flowType: FlowTypes | null;
  slices: ViadocScanSlice[];
}

const selectResolverContext = createSelector(
  selectIsDeathVerified,
  selectIndividualDesignation,
  selectIsIRASingleExecutor,
  selectIsTrustee,
  selectTypeOfTrust,
  selectIsTrustToTrustFlow,
  selectIsMultiInheritanceFlow,
  selectMultiInheritanceEstateToggle,
  (
    isDeathVerified,
    designation,
    isIRAExecutor,
    isTrustee,
    typeOfTrust,
    isTrustToTrustFlow,
    isMultiInheritanceFlow,
    isMultiInheritanceEstateToggleOn,
  ) => ({
    isDeathVerified: !!isDeathVerified,
    isExecutor: designation === IndividualDesignationListedTypes.EXECUTOR,
    isIRAExecutor: !!isIRAExecutor,
    isTrustee: !!isTrustee,
    typeOfTrust,
    isTrustToTrustFlow: !!isTrustToTrustFlow,
    isMultiInheritanceFlow: !!isMultiInheritanceFlow,
    isMultiInheritanceEstateToggleOn: !!isMultiInheritanceEstateToggleOn,
  }),
);

const selectTaxUploadStep = createSelector(
  selectDecedentInheritanceWaiverState,
  (waiverState): ViadocUploadStep =>
    waiverState === TAX_DOCUMENTATION_REQUIRED_STATES.PA ? 'itw_upload' : 'l8_upload',
);

const selectSliceFiles = createSelector(
  selectFilesMetadata,
  selectLetterFilesMetadata,
  selectTaxFilesMetadata,
  (standard, letter, tax) => ({
    standard: standard ?? [],
    letter: letter ?? [],
    tax: tax ?? [],
  }),
);

export const selectViadocScanPlan = createSelector(
  selectViadocShadowModeToggle,
  flowTypeSelection,
  selectResolverContext,
  selectIsDivorceFlow,
  selectDocumentUploadAccountTypes,
  selectTaxUploadStep,
  selectSliceFiles,
  (
    toggleOn,
    flowType,
    resolverContext,
    isDivorceFlow,
    accountTypes,
    taxUploadStep,
    sliceFiles,
  ): ViadocScanPlan => {
    const base = {
      ...resolverContext,
      isDivorceFlow: !!isDivorceFlow,
      selectedAccountTypes: accountTypes ?? [],
    };

    const candidates: Array<{ uploadStep: ViadocUploadStep; files: IFileMetaData[] }> = [
      { uploadStep: 'doc_upload', files: sliceFiles.standard },
      { uploadStep: 'letter_upload', files: sliceFiles.letter },
      { uploadStep: taxUploadStep, files: sliceFiles.tax },
    ];

    const slices = candidates
      .filter((candidate) => candidate.files.length > 0)
      .map((candidate) => ({
        uploadStep: candidate.uploadStep,
        expectedTypes: resolveExpectedLabels({ ...base, uploadStep: candidate.uploadStep }),
        files: candidate.files,
      }))
      .filter((slice) => slice.expectedTypes.length > 0);

    return { toggleOn: !!toggleOn, flowType, slices };
  },
);
